/**
 * Check-in compose — PD-CHK-01（PRODUCTIZE_PLACE）。
 * 固定 postKind=PLACE；必選 linkedLocationId；走既有 Social create API。
 * Epic D2C Batch 7：Figma `145:94` 視覺對齊（light tokens／C-THEME-01）。
 */
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { socialPostApi } from '../../api/socialPostApi';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { useAuth } from '../../auth/AuthContext';
import SafeImage from '../../components/SafeImage';
import { travelMemberProductDisplayName } from '../member-identity/memberIdentityPresentation';
import SocialLocationSelector from './SocialLocationSelector';
import SocialPostMediaEditor from './SocialPostMediaEditor';
import { hasPendingMediaUpload } from '../social-post/socialPostMediaModel';
import {
  SOCIAL_CHECKIN_KIND,
  SOCIAL_COMPOSE_VISIBILITIES,
  SOCIAL_COMPOSE_VISIBILITY_LABELS,
  SOCIAL_COMPOSE_REPLY_POLICIES,
  SOCIAL_COMPOSE_REPLY_POLICY_LABELS,
  SOCIAL_COMPOSE_QUOTE_POLICIES,
  SOCIAL_COMPOSE_QUOTE_POLICY_LABELS,
  SOCIAL_COMPOSE_DEFAULT_REPLY_POLICY,
  SOCIAL_COMPOSE_DEFAULT_QUOTE_POLICY,
  SOCIAL_TEXT_MAX,
  buildCreateSocialPostBody,
  createdSocialPostId,
  createSocialComposeClientRequestId,
  validateSocialComposeDraft,
} from './socialComposeModel';
import {
  SOCIAL_CHECKIN_PRODUCT_CHROME as checkinChrome,
  SOCIAL_COMPOSE_PRODUCT_CHROME as composeChrome,
} from '../social-post/socialActionPresentation';
import './social-compose.css';

export default function SocialCheckInComposePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const headingId = useId();
  const visibilityId = useId();
  const replyPolicyId = useId();
  const quotePolicyId = useId();
  const textId = useId();
  const pendingId = useId();
  const errorId = useId();
  const submitting = useRef(false);
  const textAreaRef = useRef(null);
  const clientRequestIdRef = useRef(createSocialComposeClientRequestId());
  const [visibility, setVisibility] = useState('PUBLIC');
  const [replyPolicy, setReplyPolicy] = useState(SOCIAL_COMPOSE_DEFAULT_REPLY_POLICY);
  const [quotePolicy, setQuotePolicy] = useState(SOCIAL_COMPOSE_DEFAULT_QUOTE_POLICY);
  const [textContent, setTextContent] = useState('');
  const [mediaItems, setMediaItems] = useState([]);
  const [locationNode, setLocationNode] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [busy, setBusy] = useState(false);

  const displayName = travelMemberProductDisplayName(user, { genericFallback: '旅人' });
  const avatarUrl = resolveMemberMediaUrl(user?.avatarUrl || user?.avatar);

  const growTextarea = useCallback(() => {
    const node = textAreaRef.current;
    if (!node) return;
    node.style.height = 'auto';
    const maxHeight = 240;
    const nextHeight = Math.min(node.scrollHeight, maxHeight);
    node.style.height = `${Math.max(nextHeight, 96)}px`;
    node.style.overflowY = node.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, []);

  useEffect(() => {
    growTextarea();
  }, [textContent, growTextarea]);

  async function onSubmit(event) {
    event.preventDefault();
    if (submitting.current || hasPendingMediaUpload(mediaItems)) return;
    const draft = {
      postKind: SOCIAL_CHECKIN_KIND,
      allowCheckInKind: true,
      visibility,
      replyPolicy,
      quotePolicy,
      textContent,
      mediaItems,
      linkedLocationId: locationNode?.locationId ?? null,
    };
    const nextErrors = validateSocialComposeDraft(draft);
    setFieldErrors(nextErrors);
    setFormError('');
    if (Object.keys(nextErrors).length) return;

    submitting.current = true;
    setBusy(true);
    try {
      const created = await socialPostApi.create(buildCreateSocialPostBody(draft, {
        clientRequestId: clientRequestIdRef.current,
      }));
      const postId = createdSocialPostId(created);
      if (!postId) throw new Error('Check-in was created without an id.');
      navigate(`/social/posts/${postId}`, { replace: true });
    } catch (cause) {
      const key = String(cause?.raw?.key || cause?.code || '');
      if (key.includes('travel.social.post.conflict') || cause?.raw?.code === 95703) {
        clientRequestIdRef.current = createSocialComposeClientRequestId();
      }
      setFormError(resolveSafeUserVisibleError(
        cause,
        () => checkinChrome.publishFailed,
        'travel.common.actionFailed',
      ));
      submitting.current = false;
      setBusy(false);
    }
  }

  return (
    <main className="social-compose social-compose--checkin social-compose--viewport" aria-labelledby={headingId} data-testid="checkin-compose">
      <header className="social-compose__topbar">
        <button
          type="button"
          className="social-compose__close"
          aria-label={checkinChrome.backToExplore}
          data-testid="checkin-compose-back"
          onClick={() => navigate('/explore')}
        >
          ‹
        </button>
        <h1 id={headingId} className="social-compose__title">{checkinChrome.title}</h1>
        <span className="social-compose__topbar-spacer" aria-hidden="true" />
      </header>
      <p className="social-compose__helper social-compose__helper--checkin">{checkinChrome.helper}</p>
      <Link className="social-compose__back-link" to="/explore">{checkinChrome.backToExplore}</Link>

      <form className="social-compose__form social-compose__form--stack" onSubmit={onSubmit} noValidate>
        <div className="social-compose__scroll" data-testid="social-compose-scroll">
        <div className="social-compose__author social-compose__author--card">
          <div className="social-compose__avatar" aria-hidden="true">
            {avatarUrl ? (
              <SafeImage src={avatarUrl} alt="" className="social-compose__avatar-img" />
            ) : (
              <span className="social-compose__avatar-fallback">{displayName.slice(0, 1)}</span>
            )}
          </div>
          <div className="social-compose__author-meta">
            <p className="social-compose__author-name">{displayName}</p>
            <p className="social-compose__audience">{checkinChrome.eyebrow}</p>
          </div>
        </div>

        <fieldset className="social-compose__field social-compose__location-card" disabled={busy}>
          <legend>{checkinChrome.locationLegend}</legend>
          <p className="social-compose__hint">{checkinChrome.locationHint}</p>
          <SocialLocationSelector
            selected={locationNode}
            onSelect={setLocationNode}
            disabled={busy}
            searchable
          />
          {fieldErrors.linkedLocationId ? (
            <span role="alert">{fieldErrors.linkedLocationId}</span>
          ) : null}
        </fieldset>

        <fieldset className="social-compose__field" disabled={busy}>
          <legend id={visibilityId}>可見範圍</legend>
          <div className="social-compose__choices" role="radiogroup" aria-labelledby={visibilityId}>
            {SOCIAL_COMPOSE_VISIBILITIES.map((value) => (
              <label key={value}>
                <input
                  type="radio"
                  name="visibility"
                  value={value}
                  checked={visibility === value}
                  onChange={() => setVisibility(value)}
                />
                {SOCIAL_COMPOSE_VISIBILITY_LABELS[value]}
              </label>
            ))}
          </div>
          {fieldErrors.visibility ? <span role="alert">{fieldErrors.visibility}</span> : null}
        </fieldset>

        <fieldset className="social-compose__field" disabled={busy}>
          <legend id={replyPolicyId}>誰可以回覆</legend>
          <div className="social-compose__choices" role="radiogroup" aria-labelledby={replyPolicyId}>
            {SOCIAL_COMPOSE_REPLY_POLICIES.map((value) => (
              <label key={value}>
                <input
                  type="radio"
                  name="replyPolicy"
                  value={value}
                  checked={replyPolicy === value}
                  onChange={() => setReplyPolicy(value)}
                />
                {SOCIAL_COMPOSE_REPLY_POLICY_LABELS[value]}
              </label>
            ))}
          </div>
        </fieldset>

        <fieldset className="social-compose__field" disabled={busy}>
          <legend id={quotePolicyId}>引用</legend>
          <div className="social-compose__choices" role="radiogroup" aria-labelledby={quotePolicyId}>
            {SOCIAL_COMPOSE_QUOTE_POLICIES.map((value) => (
              <label key={value}>
                <input
                  type="radio"
                  name="quotePolicy"
                  value={value}
                  checked={quotePolicy === value}
                  onChange={() => setQuotePolicy(value)}
                />
                {SOCIAL_COMPOSE_QUOTE_POLICY_LABELS[value]}
              </label>
            ))}
          </div>
        </fieldset>

        <div className="social-compose__field">
          <label htmlFor={textId}>內容（選填）</label>
          <textarea
            ref={textAreaRef}
            id={textId}
            className="social-compose__textarea-grow"
            value={textContent}
            onChange={(event) => setTextContent(event.target.value)}
            maxLength={SOCIAL_TEXT_MAX}
            disabled={busy}
            aria-describedby={`${textId}-count`}
          />
          <span id={`${textId}-count`} className="social-compose__count">{textContent.length} / {SOCIAL_TEXT_MAX}</span>
        </div>

        <fieldset className="social-compose__field" disabled={busy}>
          <legend>照片</legend>
          <SocialPostMediaEditor
            items={mediaItems}
            disabled={busy}
            onChange={(next) => setMediaItems((current) => (typeof next === 'function' ? next(current) : next))}
            onError={(cause) => setFormError(resolveSafeUserVisibleError(
              cause,
              () => cause?.message || composeChrome.coverUploadFailed,
              'travel.common.actionFailed',
            ))}
          />
        </fieldset>

        </div>

        {busy ? <p id={pendingId} aria-live="polite">{checkinChrome.publishing}</p> : null}
        {formError ? <p id={errorId} className="social-compose__error" role="alert" aria-live="assertive">{formError}</p> : null}

        <div className="social-compose__footer social-compose__footer--checkin social-compose__footer--sticky">
          <button
            type="submit"
            className="social-compose__publish social-compose__publish--wide"
            disabled={busy || hasPendingMediaUpload(mediaItems)}
            aria-busy={busy || hasPendingMediaUpload(mediaItems)}
          >
            {busy ? checkinChrome.publishing : checkinChrome.publish}
          </button>
          <Link className="social-compose__cancel-link" to="/explore">{checkinChrome.cancel}</Link>
        </div>
      </form>
    </main>
  );
}
